// Shared validation utilities for billing contact forms

/**
 * Validates that a billing address has all required fields and meets digit requirements
 * @param {Object} billingAddressData - The billing address data to validate
 * @returns {Object} - Object with isValid boolean and error message
 */
export const hasBillingAddressData = (billingAddressData) => {
  if (!billingAddressData) {
    return { isValid: false, error: 'Billing address data is required' };
  }
  
  // Base required fields for all countries
  const baseRequiredFields = [
    "first_name",
    "email",
    "contact_number",
    "country",
    "address_first_line",
    "city",
    "region",
    "postal_code"
  ];

  // Additional required fields only for Saudi Arabia (SA)
  const saRequiredFields = [
    "building_number",
    "plot_number",
    "vat_number",
    "registration_number"
  ];

  // Determine required fields based on country
  const requiredFields = billingAddressData.country === 'SA' 
    ? [...baseRequiredFields, ...saRequiredFields]
    : baseRequiredFields;

  // Check if all required fields exist
  const missingFields = requiredFields.filter(field => !billingAddressData[field]);
  
  if (missingFields.length > 0) {
    return { 
      isValid: false, 
      error: `Missing required fields: ${missingFields.join(', ')}` 
    };
  }

  const email = billingAddressData.email

  if (email && !validateEmailField(email)) {
    return { 
      isValid: false, 
      error: 'Invalid email format' 
    };
  }

  if (billingAddressData.country !== 'SA') {
    // For other countries, just check that required fields exist
    return { isValid: true, error: null };
  }
  
  // Only apply digit validation for Saudi Arabia (SA)
  const buildingNumber = billingAddressData.building_number?.toString() || '';
  const plotNumber = billingAddressData.plot_number?.toString() || '';
  const postalCode = billingAddressData.postal_code?.toString() || '';
  const vatNumber = billingAddressData.vat_number?.toString() || '';

  // Check if building_number has exactly 4 digits
  const isBuildingNumberValid = /^\d{4}$/.test(buildingNumber);
  if (!isBuildingNumberValid) {
    return { 
      isValid: false, 
      error: 'Building number must be exactly 4 digits for Saudi Arabia' 
    };
  }

  // Check if plot_number has exactly 4 digits
  const isPlotNumberValid = /^\d{4}$/.test(plotNumber);
  if (!isPlotNumberValid) {
    return { 
      isValid: false, 
      error: 'Plot number must be exactly 4 digits for Saudi Arabia' 
    };
  }

  // Check if postal_code has exactly 5 digits
  const isPostalCodeValid = /^\d{5}$/.test(postalCode);
  if (!isPostalCodeValid) {
    return { 
      isValid: false, 
      error: 'Postal code must be exactly 5 digits for Saudi Arabia' 
    };
  }

  // Check if vat_number has exactly 15 digits, starts with 3, and ends with 3
  const isVatNumberValid = /^3\d{13}3$/.test(vatNumber);
  if (!isVatNumberValid) {
    return { 
      isValid: false, 
      error: 'VAT number must be exactly 15 digits, start with 3, and end with 3 for Saudi Arabia' 
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validates VAT number for Saudi Arabia (SA)
 * @param {string} value - The VAT number to validate
 * @param {string} country - The country code to determine validation rules
 * @returns {string} - The validated VAT number
 */
export const validateVatNumber = (value, country = null) => {
  // Only apply VAT validation for Saudi Arabia (SA)
  if (country === 'SA') {
    const numericValue = value.replace(/\D/g, ''); // Remove non-digits
    return numericValue.slice(0, 15); // Limit to 15 digits
  }
  
  // For other countries, return the value as-is
  return value;
};

/**
 * Validates numeric fields by removing non-digits and limiting length
 * @param {string} value - The input value to validate
 * @param {number} maxLength - Maximum number of digits allowed
 * @param {string} country - The country code to determine validation rules
 * @returns {string} - The validated numeric string
 */
export const validateNumericField = (value, maxLength, country = null) => {
  // Only apply digit validation for Saudi Arabia (SA)
  if (country === 'SA') {
    const numericValue = value.replace(/\D/g, ''); // Remove non-digits
    return numericValue.slice(0, maxLength); // Limit to maxLength
  }
  
  // For other countries, return the value as-is
  return value;
};

export const validateEmailField = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  const trimmedValue = value.trim();
  
  if (!trimmedValue) {
    return false;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  return emailRegex.test(trimmedValue);
}
